// Byer: forbrug, ønsker, vækst, mørkeur og tillid.

import { difficulty } from './difficulty';
import { cashFactor, GROWTH_GRANT_PER_100MW } from './economy';
import { heatDemand, shedNow, spikePct, uflsShed } from './effects';
import { markDirty } from './flow';
import type { CityState, CityTier, DarkCause, DifficultyDef, GridNode, LevelDef, SimEvent, State } from './types';
import { TICK_HZ } from './types';
import { DEMAND_PM, demandPm, seasonIndex, seasonOf } from './weather';

/** Grundforbrug pr. trin: landsby, by 5 huse, by 7 huse, storby 10, storby 12. */
export const TIER_MW: readonly number[] = [80, 150, 250, 400, 600];
export const DATACENTER_MW = 300;
/** Uret fylder med CLOCK_SCALE pr. tick ved fuldt underskud (mindst halv fart). */
export const CLOCK_SCALE = 100;
/** Uret tømmes med dobbelt fart. */
export const CLOCK_DRAIN = 2 * CLOCK_SCALE;
/** En by i blackout tænder igen tidligst efter 10 s. */
export const DARK_TICKS = 100;
/**
 * Efter et udfald må ingen anden by gå ud de næste 4 s: dens ur står og venter lige under fuldt.
 * Playtest runde 2: to udfald i samme sekund tog begge hjerter, før spilleren kunne nå at handle.
 */
export const BLACKOUT_GAP_TICKS = 40;

/** En by, der ikke har fået strøm igen siden sit udfald, kan tidligst koste et nyt hjerte efter 20 s (10 s mørk plus 10 s til at sende en montør). */
export const SAME_CITY_RESPITE_TICKS = 200;

/** Pausen mellem to udfald på sværheden (Lærling og Vagthavende: 15 s, ét hjerte pr. hændelse). */
export function blackoutGapTicks(state: State): number {
  const s = difficulty(state.difficulty).blackoutGapS;
  return s === undefined ? BLACKOUT_GAP_TICKS : s * TICK_HZ;
}
/** En ny by får 10 s, før uret må fylde. */
export const GRACE_TICKS = 100;
/**
 * Frist for byerne fra start og for nye byer i banens første år. Lærling og Vagthavende giver 20 s og
 * 15 s (difficulty.ts); de svære niveauer beholder 10 s. Playtest runde 3: på Vagthavende mistede en
 * ny spiller et hjerte, før hun havde fundet ud af at trække en ledning.
 */
export function startGraceTicks(d: { startGraceS?: number }): number {
  return d.startGraceS !== undefined ? d.startGraceS * 10 : GRACE_TICKS;
}
/**
 * Frist for en ny by. År 0 bruger firstYearGraceS, de senere år laterGraceS (IGN-løkke 2: på Orkan
 * kunne en by dukke op og være mørk, før man havde set den, og så føltes hjertet tabt uden valg).
 */
export function newCityGraceTicks(d: { firstYearGraceS?: number; laterGraceS?: number }, year: number): number {
  if (year === 0 && d.firstYearGraceS !== undefined) return d.firstYearGraceS * 10;
  if (year > 0 && d.laterGraceS !== undefined) return d.laterGraceS * 10;
  return GRACE_TICKS;
}
/** Under 90 % leverance fylder uret. */
export const SERVED_OK_PM = 900;
export const EV_FACTOR_PM = 1300;
export const HEAT_FACTOR_PM = 1400;
/** Makkerspillets ekstra forbrug i promille, fra sværhedstabellen (Lærling 1000, Orkan 1400). */
export function coopDemandPm(state: State): number {
  return state.coop ? Math.round(difficulty(state.difficulty).coopDemand * 1000) : 1000;
}
/** Hedebølge uden planlagt køling: byer og datacentre bruger 10 % mere. */
export const HEAT_DEMAND_PM = 1100;

export function clockFull(state: State): number {
  return difficulty(state.difficulty).clockS * TICK_HZ * CLOCK_SCALE;
}

export function newCity(tier: CityTier, maxTier: CityTier, baseMw?: number): CityState {
  return {
    tier,
    baseMw: baseMw ?? TIER_MW[tier] ?? 80,
    demandMw: 0,
    servedMw: 0,
    clock: 0,
    maxClockPm: 0,
    darkTicks: 0,
    out: false,
    graceTicks: 0,
    evTicks: 0,
    happyThisYear: true,
    dimTicks: 0,
    blackouts: 0,
    maxTier,
    rampMw: 0,
    rampStep: 0,
    fedTicks: 0,
    grewAt: 0,
  };
}

export function newDatacenter(baseMw?: number): CityState {
  const c = newCity(0, 0, baseMw ?? DATACENTER_MW);
  return c;
}

/** Forbruget lige nu i MW for en forbruger (før blackout-pausen). */
export function cityDemand(state: State, level: LevelDef, n: GridNode): number {
  const c = n.city;
  if (!c || !n.active || n.phantom) return 0;
  // Nødaflastning: byen er slukket med vilje, og uret står stille.
  if (shedNow(state, n) || uflsShed(state, n.id)) return 0;
  const heat = heatDemand(state) ? HEAT_DEMAND_PM : 1000;
  if (n.kind === 'datacenter') return Math.round((((c.baseMw * coopDemandPm(state)) / 1000) * heat) / 1000);
  let pm = demandPm(state);
  if (level.mechanics.includes('wishes') && seasonOf(state.clockTick) === 3) pm = Math.round((pm * HEAT_FACTOR_PM) / 1200);
  let mw = (c.baseMw * pm) / 1000;
  if (c.evTicks > 0) mw = (mw * EV_FACTOR_PM) / 1000;
  const spike = spikePct(state, n.id);
  if (spike > 0) mw = (mw * (100 + spike)) / 100;
  mw = (mw * heat) / 1000;
  if (state.coop) mw = (mw * coopDemandPm(state)) / 1000;
  return Math.round(mw);
}

/** Opdatér alle byers forbrug. Markerer flowet beskidt, hvis noget ændrede sig. */
export function updateDemand(state: State, level: LevelDef): void {
  let changed = false;
  const second = state.clockTick % TICK_HZ === 0;
  for (const n of state.nodes) {
    if (!n.city) continue;
    if (n.city.evTicks > 0) n.city.evTicks--;
    if (second && n.city.rampMw > 0) {
      const add = Math.min(n.city.rampMw, n.city.rampStep);
      n.city.baseMw += add;
      n.city.rampMw -= add;
    }
    const d = cityDemand(state, level, n);
    if (d !== n.city.demandMw) {
      n.city.demandMw = d;
      changed = true;
    }
  }
  if (changed) markDirty(state);
}

/** Har byen strøm nok (mindst 90 %)? */
export function isPowered(n: GridNode): boolean {
  const c = n.city;
  if (!c) return true;
  if (c.demandMw <= 0) return true;
  return c.servedMw * 1000 >= c.demandMw * SERVED_OK_PM;
}

/**
 * Makkerspil: de første 25 s af banen står urene stille, mens I finder ud af, hvem der gør hvad.
 * Playtest runde 2: den første by gik i sort, før de to havde fået talt sammen.
 */
export const COOP_GRACE_TICKS = 250;

export function clocksFrozen(state: State, level: LevelDef): boolean {
  if (state.coop && state.year === 0 && state.clockTick < COOP_GRACE_TICKS) return true;
  return state.year < level.clockFrozenUntilYear;
}

/**
 * Mørkeure: fyld ved underskud (et lille underskud fylder langsomt), tøm ved strøm, blackout ved fuldt ur. Kaldes én gang pr. tick efter
 * flowet. Returnerer hvor meget tillid der blev brugt.
 */
/**
 * Ticks til byen går i sort med det underskud, den har lige nu, eller null når uret ikke løber (byen
 * har strøm, er mørk i forvejen, har frist, eller urene står stille). Bykortet og træneren bruger det
 * til at finde byen, der er nærmest at gå ud (IGN runde 1). Ren funktion af state.
 */
export function darkInTicks(state: State, level: LevelDef, n: GridNode): number | null {
  const c = n.city;
  if (!c || !n.active || n.phantom || c.darkTicks > 0 || isPowered(n) || c.graceTicks > 0 || clocksFrozen(state, level)) return null;
  const floor = difficulty(state.difficulty).clockFloorPm;
  const deficitPm = c.demandMw > 0 ? Math.round(((c.demandMw - c.servedMw) * 1000) / c.demandMw) : 0;
  const rate = Math.round((CLOCK_SCALE * Math.max(floor, deficitPm)) / 1000);
  if (rate <= 0) return null;
  return Math.max(0, Math.ceil((clockFull(state) - c.clock) / rate));
}

/** En redning: uret vender med højst 25 % tilbage. */
export const RESCUE_LEFT_PCT = 25;

export function updateClocks(state: State, level: LevelDef, events: SimEvent[]): number {
  const full = clockFull(state);
  const floor = difficulty(state.difficulty).clockFloorPm;
  const frozen = clocksFrozen(state, level);
  let lost = 0;
  for (const n of state.nodes) {
    const c = n.city;
    // Fantomer får et mørkeur som alle andre, så de ikke afslører sig selv. Når det er fuldt, trækker
    // fantomet sig stille ud af køen: ingen tillid tabt.
    if (!c || !n.active) continue;
    if (c.graceTicks > 0) c.graceTicks--;
    if (c.darkTicks > 0) {
      if (!n.phantom) c.dimTicks++;
      c.darkTicks--;
      if (c.darkTicks === 0) markDirty(state);
      continue;
    }
    const powered = !n.phantom && isPowered(n);
    // Et beskidt flow er et par ticks bagud (fx lige efter et årstidsskifte): det tæller ikke som underskud.
    if (powered && !c.out && c.servedMw > 0) c.fedTicks++;
    else if (!state.flow.dirty) c.fedTicks = 0;
    if (powered) {
      if (c.out) {
        c.out = false;
        events.push({ t: 'relit', tick: state.tick, node: n.id });
      }
      if (c.climbing) {
        // Uret vender: med højst en fjerdedel tilbage er det en redning (PUH!).
        delete c.climbing;
        // Mindst 1 %: med Orkans korte ur kan en redning i sidste tick ellers runde til "0 % tilbage".
        const leftPct = Math.max(1, Math.round(((full - c.clock) * 100) / full));
        if (c.clock > 0 && leftPct <= RESCUE_LEFT_PCT) events.push({ t: 'rescue', tick: state.tick, node: n.id, leftPct });
      }
      if (c.clock > 0) c.clock = Math.max(0, c.clock - CLOCK_DRAIN);
      continue;
    }
    if (frozen || c.graceTicks > 0) continue;
    if (!n.phantom) c.dimTicks++;
    const deficitPm = c.demandMw > 0 ? Math.round(((c.demandMw - c.servedMw) * 1000) / c.demandMw) : 0;
    const rate = Math.round((CLOCK_SCALE * Math.max(floor, deficitPm)) / 1000);
    if (c.clock === 0) events.push({ t: 'clock_started', tick: state.tick, node: n.id });
    c.clock = Math.min(full, c.clock + rate);
    if (rate > 0) c.climbing = true;
    if (n.phantom) {
      if (c.clock >= full) {
        c.clock = 0;
        n.active = false;
        markDirty(state);
        events.push({ t: 'phantom_vanished', tick: state.tick, node: n.id });
      }
      continue;
    }
    const pm = Math.round((c.clock * 1000) / full);
    if (pm > c.maxClockPm) c.maxClockPm = pm;
    if (pm > 500) c.happyThisYear = false;
    // Samme by, der stadig er mørk efter sit udfald, koster ikke et hjerte mere de første 20 s (IGN runde 4:
    // Systemansvarlig tog to hjerter på 8 s for ét vedvarende udfald, før en montør kunne nå frem).
    const last = state.lastBlackout;
    const respite = last && (state.tick - last.tick < blackoutGapTicks(state) || (c.out && last.node === n.id && state.tick - last.tick < SAME_CITY_RESPITE_TICKS));
    if (c.clock >= full && respite) {
      c.clock = full - 1;
      continue;
    }
    if (c.clock >= full) {
      c.clock = 0;
      delete c.climbing;
      c.darkTicks = DARK_TICKS;
      c.out = true;
      c.blackouts++;
      c.happyThisYear = false;
      // Kun når spilleren faktisk har bygget: en helt passiv spiller taber stadig.
      const d = difficulty(state.difficulty);
      const built = state.lines.length > level.lines.length;
      const spared = state.trust === 1 && level.number <= (d.spareLevels ?? 0) && built;
      // IGN-løkke 2, runde 3: banens første udfald er på huset (Orkan i år 0,
      // Lærling fra bane 4), så en bane tabes på beslutninger, ikke på det første uvejr. Orkan: kun et brud.
      const cause = darkCause(state, n);
      const grace = !spared && built && state.stats.blackouts === 0 && graceFor(d, level.number, state.year, cause);
      if (!spared && !grace) state.trust = Math.max(0, state.trust - 1);
      state.yearStats.blackouts++;
      state.stats.blackouts++;
      lost++;
      state.lastBlackout = { node: n.id, cause, season: seasonOf(state.clockTick), tick: state.tick };
      markDirty(state);
      events.push({ t: 'blackout', tick: state.tick, node: n.id, trustLeft: state.trust, cause, ...(spared ? { spared: true as const } : {}), ...(grace ? { grace: true as const } : {}) });
    }
  }
  return lost;
}

/** Er banens første udfald gratis på denne sværhed, bane og år? (DifficultyDef.graceFirst) */
export function graceFor(d: DifficultyDef, levelNumber: number, year: number, cause?: DarkCause): boolean {
  const g = d.graceFirst;
  if (!g) return false;
  if (g.causes && (!cause || !g.causes.includes(cause))) return false;
  return levelNumber >= (g.fromLevel ?? 1) && (g.untilYear === undefined || year <= g.untilYear);
}

/** Væksten når ud i forbruget over 30 s i stedet for på én gang. */
export const RAMP_S = 30;

/**
 * Sæt et nyt trin for en by. Væksten (forskellen i MW) ganges med sværhedens vækstfaktor, halveres i
 * de rolige første år og lægges til forbruget lidt ad gangen. Returnerer den samlede vækst i MW.
 */
export function growCity(state: State, n: GridNode, tier: CityTier, events: SimEvent[]): number {
  const c = n.city;
  if (!c || tier <= c.tier) return 0;
  const add = growthMw(state, c, tier);
  c.rampMw += add;
  c.rampStep = Math.max(1, Math.ceil(c.rampMw / RAMP_S));
  c.tier = tier;
  c.grewAt = state.clockTick;
  if (tier > c.maxTier) c.maxTier = tier;
  markDirty(state);
  events.push({ t: 'city_grew', tick: state.tick, node: n.id, tier });
  return add;
}

/** Hvor mange MW byen vokser med ved at gå til `tier` (efter sværhedens vækstfaktor). */
export function growthMw(state: State, c: CityState, tier: CityTier): number {
  const d = difficulty(state.difficulty);
  const growth = state.year < d.calmYears ? d.demandGrowth / 2 : d.demandGrowth;
  const delta = (TIER_MW[tier] ?? 0) - (TIER_MW[c.tier] ?? 0);
  return Math.max(0, Math.round(delta * growth));
}

/** Reserve i promille af områdets forbrug, der skal være til overs efter en vækst. */
export const GROW_RESERVE_PM = 100;
/** Årstider frem, væksten skal kunne dækkes i (et helt år: også den stille sommer). */
export const GROW_LOOKAHEAD_SEASONS = 4;

/**
 * Den mindste overskudsproduktion i området over det næste år: for hver årstid produktionen med
 * årstidens vind og sol minus byernes grundforbrug (inklusive vækst på vej) med årstidens faktor og
 * en reserve på 10 %. Vejret er kendt (seedet ved start), så en by vokser kun, hvis nettet kan bære
 * den hele året; spilleren åbner for mere vækst ved at bygge produktion.
 */
export function areaHeadroom(state: State, area: GridNode['area']): number {
  const i0 = seasonIndex(state.clockTick);
  let worst = Number.MAX_SAFE_INTEGER;
  for (let k = 0; k < GROW_LOOKAHEAD_SEASONS; k++) {
    const i = Math.min(i0 + k, state.weather.windPm.length - 1);
    const wind = state.weather.windPm[i] ?? 600;
    const sun = state.weather.sunPm[i] ?? 500;
    const demPm = DEMAND_PM[(i0 + k) % 4] ?? 1000;
    let gen = 0;
    let dem = 0;
    for (const n of state.nodes) {
      if (!n.active || n.phantom || n.area !== area) continue;
      const p = n.producer;
      if (p) {
        if (n.kind === 'wind') gen += (p.capMw * wind) / 1000;
        else if (n.kind === 'solar') gen += (p.capMw * sun) / 1000;
        else if (n.kind === 'offshore') gen += (p.capMw * Math.min(1000, wind + 150)) / 1000;
        else gen += p.capMw;
      }
      const c = n.city;
      if (c && n.kind === 'city') dem += ((c.baseMw + c.rampMw) * demPm) / 1000;
      else if (c) dem += c.baseMw + c.rampMw;
    }
    worst = Math.min(worst, Math.round(gen - dem - (dem * GROW_RESERVE_PM) / 1000));
  }
  return worst;
}

/** Ticks mellem to vækstpulser. */
export function growthTicks(state: State): number {
  return Math.max(1, difficulty(state.difficulty).growthEveryS * TICK_HZ);
}

/** En by skal have haft fuld strøm i 20 s, før den må vokse. */
export const GROW_FED_TICKS = 200;
/** Ingen vækstpuls, hvis en ny kunde dukkede op inden for de sidste 10 s. */
export const GROW_QUIET_TICKS = 100;

/**
 * Vækstpuls hvert `growthEveryS` sekund: den by med fuld strøm i mindst 20 s, der har ventet længst
 * siden sin seneste vækst, vokser ét trin (op til sit loft), hvis området har produktion nok til
 * væksten. Tilskuddet betales med det samme.
 * Højst én by pr. puls, ingen puls inden for 10 s efter en ny kunde og ingen i finalen. Returnerer
 * væksten i MW.
 */
export function growthPulse(state: State, events: SimEvent[]): number {
  if (state.finale || state.clockTick < state.nextGrowthAt) return 0;
  // Lige efter en ny kunde venter pulsen, til der har været 10 s ro (den springes ikke helt over,
  // ellers ville den på Lærling ramme de første kunder hver gang og aldrig komme).
  if (state.nodes.some((n) => n.active && n.activeSince > 0 && state.tick - n.activeSince < GROW_QUIET_TICKS)) return 0;
  state.nextGrowthAt = state.clockTick + growthTicks(state);
  let best: GridNode | null = null;
  for (const n of state.nodes) {
    const c = n.city;
    if (!c || !n.active || n.phantom || n.kind !== 'city') continue;
    if (c.out || c.fedTicks < GROW_FED_TICKS || c.tier >= c.maxTier) continue;
    // Kun vækst, der kan dækkes: findes strømmen slet ikke i området, kan ingen ledning redde byen.
    if (areaHeadroom(state, n.area) < growthMw(state, c, (c.tier + 1) as CityTier)) continue;
    if (!best || c.grewAt < (best.city as CityState).grewAt) best = n;
  }
  if (!best) return 0;
  const c = best.city as CityState;
  const add = growCity(state, best, (c.tier + 1) as CityTier, events);
  const grant = Math.round(((GROWTH_GRANT_PER_100MW * add) / 100) * cashFactor(state));
  state.budget += grant;
  state.yearStats.grownMw += add;
  state.yearStats.grant += grant;
  return add;
}

/** Byens net over hele ledninger (inklusive byen selv). */
export function netOf(state: State, from: string): Set<string> {
  const seen = new Set<string>([from]);
  const stack = [from];
  while (stack.length > 0) {
    const u = stack.pop() as string;
    for (const l of state.lines) {
      if (l.status !== 'ok') continue;
      const v = l.a === u ? l.b : l.b === u ? l.a : null;
      if (v !== null && !seen.has(v)) {
        seen.add(v);
        stack.push(v);
      }
    }
  }
  return seen;
}

/**
 * Hvorfor mangler byen strøm? Ingen ledning overhovedet, skåret fra af et brud, ledninger der er for
 * små (der er ledig produktion i nettet, men en ledning står fuld), eller for lidt produktion.
 */
export function darkCause(state: State, n: GridNode): DarkCause {
  if (!state.lines.some((l) => l.a === n.id || l.b === n.id)) return 'unconnected';
  const net = netOf(state, n.id);
  let producers = 0;
  let spare = 0;
  for (const m of state.nodes) {
    if (!m.producer || !m.active || !net.has(m.id)) continue;
    producers++;
    spare += Math.max(0, m.producer.availMw - m.producer.usedMw);
  }
  if (producers === 0) {
    const broken = state.lines.some((l) => l.status === 'broken' && (net.has(l.a) || net.has(l.b)));
    return broken ? 'cut' : 'unconnected';
  }
  const full = state.lines.some((l) => l.status === 'ok' && net.has(l.a) && net.has(l.b) && l.loadPm >= 950);
  const want = Math.max(0, (n.city?.demandMw ?? 0) - (n.city?.servedMw ?? 0));
  if (full && spare >= Math.max(10, want / 4)) return 'lines';
  // Et brud på kanten af nettet (eller en halvt nedblæst ledning, der står fuld) har skåret en del af
  // forsyningen fra. Det er bruddet, ikke vind og sol, der svigtede (playtest runde 3: "Vind og sol
  // rakte ikke", mens stormen havde knækket byens ledning ud til møllerne).
  const cut = state.lines.some(
    (l) => (l.status === 'broken' && net.has(l.a) !== net.has(l.b)) || (l.status === 'ok' && l.halfDown && net.has(l.a) && l.loadPm >= 950),
  );
  if (cut) return 'cut';
  return 'power';
}
